import { useEffect } from 'react';
import type { GroupModel } from 'entities/campaign-groups';
import { useGetCampaignGroups } from 'entities/campaign-groups';
import { pageRoutes } from 'shared/routes';
import { AlertBox } from 'shared/ui/AlertBox';
import { ClippedText } from 'shared/ui/ClippedText';
import { Loader } from 'shared/ui/Loader';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import { PercentageCell, Table } from 'shared/ui/Table';
import type { Column } from 'shared/ui/Table';
import { Tooltip } from 'shared/ui/Tooltip';
import { isValidationErrorsModel } from 'shared/lib/api';
import { showDefaultErrorToast } from 'shared/lib/notifications';
import { pathToUrl } from 'shared/lib/router';
import { formatDecimal } from 'shared/lib/formatting';

interface GetColumnsProps {
  onGoToGroupClick: (id: number) => void;
}

const getColumns = ({ onGoToGroupClick }: GetColumnsProps): Column<GroupModel>[] => {
  return [
    {
      label: 'GROUP',
      key: 'groups',
      columnCell: ({ row }) => (
        <td className="min-w-[106px] max-w-[106px] !py-3 text-[13px]">
          <ClippedText
            tooltip={{
              placement: 'bottom-start',
              content: <span className="block text-left text-[13px]">{row.name}</span>,
              className: 'break-all',
            }}
            text={row.name}
          />
        </td>
      ),
      headerCell: ({ value }) => <th className="text-[10px]">{value}</th>,
    },
    {
      label: 'TRAFFIC...',
      key: 'traffic',
      columnCell: ({ row }) => {
        const days: Record<string, string> = {
          'monday-friday': 'Mon - Fri',
          'monday-saturday': 'Mon - Sat',
          'monday-sunday': 'Mon - Sun',
          'saturday-sunday': 'Sat - Sun',
          saturday: 'Sat',
          sunday: 'Sun',
        };

        return (
          <td className="min-w-[90px] !py-3 !pr-[10px] text-[13px]">{days[row.trafficDays] || row.trafficDays}</td>
        );
      },
      headerCell: ({ value }) => (
        <th className="text-[10px]">
          <Tooltip
            placement="bottom-start"
            content="Traffic Days"
            className="cursor-pointer"
          >
            {value as string}
          </Tooltip>
        </th>
      ),
    },
    {
      label: 'ZIPS IN USE',
      key: 'zips',
      columnCell: ({ row }) => (
        <PercentageCell
          className="min-w-[94px] truncate !py-3 !pr-5"
          title={formatDecimal(row.counters.zipCodesInUse)}
          percent={row.counters.percentZipCodesInUse}
          total={formatDecimal(row.counters.totalZipCodes)}
        />
      ),
      headerCell: ({ value }) => <th className="text-[10px]">{value}</th>,
    },
    {
      label: '',
      key: 'btn',
      columnCell: ({ row }) => (
        <td className="w-52 !p-0 !pl-3 !pt-1">
          <MaterialIcon
            className="cursor-pointer select-none !text-[20px] text-slate-300 hover:text-deep-blue-500"
            onClick={() => onGoToGroupClick(row.id)}
          >
            open_in_new
          </MaterialIcon>
        </td>
      ),
      headerCell: () => <th className="!p-0"> </th>,
    },
  ];
};

export function GroupsField({ id }: { id: number }) {
  const getCampaignGroupsQuery = useGetCampaignGroups(id);
  const campaignGroups = getCampaignGroupsQuery.data?.data;

  const onGoToGroupClick = (groupId: number) => {
    window.open(pathToUrl(pageRoutes.internal.singleView, { groupId: groupId.toString() }));
  };

  const columns = getColumns({ onGoToGroupClick });

  useEffect(() => {
    if (isValidationErrorsModel(getCampaignGroupsQuery.error?.response.data)) {
      showDefaultErrorToast();
    }
  }, [getCampaignGroupsQuery.error]);

  if (getCampaignGroupsQuery.isFetching) {
    return <Loader className="mt-6 flex justify-center" />;
  }

  if (getCampaignGroupsQuery.isError) {
    return (
      <>
        <div className="my-8 border-t border-slate-100" />
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold">Groups</div>
        </div>
        <AlertBox
          className="mt-8 border-red-100"
          iconClass="text-red-500"
          iconName="info"
        >
          <div className="text-sm font-medium">Oops!</div>
          <div className="mt-1 text-xs font-normal text-slate-600">
            We couldn't load related groups, please try again later.
          </div>
        </AlertBox>
      </>
    );
  }

  if (campaignGroups?.length === 0) {
    return null;
  }

  return (
    <>
      <div className="my-8 border-t border-slate-100" />
      <div className="flex items-center justify-between">
        <div className="text-lg font-semibold">Groups</div>
      </div>
      <Table
        className="!mt-4"
        variant="table-sm"
        columns={columns}
        data={campaignGroups}
        rowKey={(data) => data.id}
        rowClassName={() => '[&:has(span:hover)]:bg-deep-blue-25'}
      />
    </>
  );
}
